import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Check, ArrowRight, ArrowLeft, Loader2, CalendarCheck, Plus, X, Car, Truck, Crown, UserPlus, Cake } from "lucide-react";
import { Logo } from "@/components/layout/Logo";
import Seo from "@/components/common/Seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { TIME_SLOTS } from "@/data/site";
import { useAuth } from "@/context/AuthContext";

const WD = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MO = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const VEHICLES = [{ id: "sedan", label: "Sedan" }, { id: "suv", label: "SUV" }, { id: "van", label: "Van / Truck" }];
const SERVICES = [
  { id: "interior", label: "Interior Detail", desc: "Deep clean inside" },
  { id: "full", label: "Full Detail", desc: "Inside + outside" },
  { id: "express", label: "Express 90-Min", desc: "Quick refresh" },
  { id: "ceramic", label: "Ceramic Coating", desc: "Multi-year protection" },
];
const CONDITIONS = [
  { id: "light", label: "Light", desc: "Well maintained, quick refresh" },
  { id: "moderate", label: "Moderate", desc: "Normal daily use, some dirt" },
  { id: "heavy", label: "Heavy", desc: "Pet hair, salt, stains" },
];
const CERAMIC_TIERS = [
  { id: "shine", label: "Shine & Seal", price: "$249 · 1-yr" },
  { id: "pro", label: "Pro Coat", price: "$899 · 3-yr" },
  { id: "elite", label: "Elite Coat", price: "$1,299 · 5-yr" },
];
const PLANS = [
  { id: "one_time", name: "One-Time", freq: "Just this once", off: 0 },
  { id: "seasonal", name: "Seasonal", freq: "4× / year", off: 5 },
  { id: "monthly", name: "Monthly", freq: "Every month", off: 10 },
  { id: "biweekly", name: "Bi-Weekly", freq: "Every 2 weeks", off: 15, badge: "Most Popular" },
  { id: "weekly", name: "Weekly", freq: "Every week", off: 20 },
  { id: "vip", name: "Annual VIP", freq: "12 prepaid + priority", off: 25 },
];
const STEPS = ["Vehicle", "Service", "Condition", "Plan", "Date & Place", "Your Details"];

function prettyDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return `${WD[dt.getDay()]}, ${MO[dt.getMonth()]} ${dt.getDate()}`;
}

export default function Booking() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [availability, setAvailability] = useState([]);
  const [availMeta, setAvailMeta] = useState({ isMember: false, lead: 2 });
  const [step, setStep] = useState(0);
  const [confirmed, setConfirmed] = useState(null);
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState(null);

  const [vehicles, setVehicles] = useState(["sedan"]);
  const [service, setService] = useState(params.get("service") || "full");
  const [ceramicTier, setCeramicTier] = useState("shine");
  const [condition, setCondition] = useState("moderate");
  const [plan, setPlan] = useState("one_time");
  const [date, setDate] = useState(params.get("date") || "");
  const [slot, setSlot] = useState("");
  const [postal, setPostal] = useState("");
  const [details, setDetails] = useState({ name: "", email: "", phone: "", address: "", notes: "" });

  useEffect(() => {
    api.get("/availability").then(({ data }) => {
      setAvailability(data.days || []);
      setAvailMeta({ isMember: !!data.is_member, lead: data.priority_lead_days || 2 });
    });
  }, [user]);
  useEffect(() => { if (user) setDetails((d) => ({ ...d, name: d.name || user.name || "", email: d.email || user.email || "" })); }, [user]);

  const fetchQuote = useCallback(async () => {
    try {
      const { data } = await api.post("/quote", { vehicles, service, plan, ceramic_tier: ceramicTier });
      setQuote(data);
    } catch { /* ignore */ }
  }, [vehicles, service, plan, ceramicTier]);
  useEffect(() => { fetchQuote(); }, [fetchQuote]);

  const subAllowed = service === "interior" || service === "full";
  const upd = (k) => (e) => setDetails((d) => ({ ...d, [k]: e.target.value }));

  const canNext =
    (step === 0 && vehicles.length > 0) ||
    (step === 1 && service) ||
    (step === 2 && condition) ||
    (step === 3 && plan) ||
    (step === 4 && date && slot && postal) ||
    (step === 5 && details.name && details.email && details.phone && details.address);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", {
        vehicles, service, condition, plan: subAllowed ? plan : "one_time",
        ceramic_tier: ceramicTier, date, time_slot: slot, postal_code: postal,
        origin_url: window.location.origin, ...details,
      });
      setConfirmed(data);
      window.scrollTo(0, 0);
      if (data.referral_message) toast.success(data.referral_message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit.");
    } finally { setBusy(false); }
  };

  if (confirmed) {
    return (
      <div data-testid="booking-confirmed" className="container-rv flex min-h-[75vh] flex-col items-center justify-center py-24 text-center">
        <Seo title="Booking Confirmed | Reverence Autos" description="Your detail is booked." path="/book" />
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 15 }}
          className="grid h-20 w-20 place-items-center rounded-full bg-brand-red text-white">
          <CalendarCheck className="h-9 w-9" />
        </motion.div>
        <h1 className="mt-8 font-serif text-4xl font-bold text-brand-charcoal sm:text-5xl">You're booked — here's what happens next.</h1>
        <p className="mt-4 max-w-md text-brand-muted">
          We've emailed your confirmation. Our crew will text to confirm your slot on <b>{prettyDate(confirmed.date)}</b>, arrive in your chosen window, and you pay only after your car looks incredible. No payment today.
        </p>
        <div className="mt-8 w-full max-w-sm rounded-2xl border border-brand-silver/40 bg-brand-offwhite p-6 text-left text-sm">
          {confirmed.pricing.items.map((it, i) => (
            <div key={i} className="flex justify-between py-1"><span className="text-brand-muted">{it.service} · {it.label}</span><span>${it.final.toFixed(2)}</span></div>
          ))}
          <div className="my-3 silver-divider" />
          <div className="flex justify-between text-brand-red"><span>You saved today</span><span>– ${confirmed.pricing.savings.toFixed(2)}</span></div>
          <div className="mt-2 flex justify-between font-serif text-lg font-bold"><span>Total due</span><span>${confirmed.pricing.total.toFixed(2)} <span className="text-sm font-normal text-brand-muted">+ tax</span></span></div>
        </div>
        {confirmed.account_prompt && (
          <div className="mt-8 rounded-2xl border border-brand-red/40 bg-brand-red/5 p-6 text-center">
            <p className="font-semibold text-brand-charcoal">Create an account to track this booking</p>
            <p className="mt-1 text-sm text-brand-muted">Manage visits, earn loyalty rewards and referral credits.</p>
            <Link to="/signup" className="btn-red mt-4" data-testid="confirm-create-account">Create account</Link>
          </div>
        )}
        <div className="mt-8 flex gap-4">
          <Link to="/" className="btn-outline-dark">Back home</Link>
          {user && <Link to="/dashboard" className="btn-red">Go to dashboard</Link>}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="booking-page" className="bg-brand-offwhite">
      <Seo title="Book Your Detail in 60 Seconds | Reverence Autos" description="Book mobile car detailing in Ottawa in under 60 seconds. Choose your vehicle, service and date — we come to you." path="/book" />
      <div className="container-rv py-14 lg:py-20">
        <div className="mb-8 flex items-center justify-between">
          <Logo />
          <span className="hidden text-sm text-brand-muted sm:block">Book in 60 seconds</span>
        </div>

        {date && (
          <div className="mb-6 flex flex-wrap items-center gap-2 rounded-xl border border-brand-red/30 bg-brand-red/5 px-4 py-3 text-sm">
            <CalendarCheck className="h-4 w-4 text-brand-red" />
            <span className="font-medium text-brand-charcoal" data-testid="booking-prefilled-date">Booking for {prettyDate(date)}</span>
            <button onClick={() => { setDate(""); setStep(4); }} className="text-brand-red underline underline-offset-2" data-testid="change-date">— change date</button>
          </div>
        )}

        {/* progress */}
        <div className="mb-10 h-1.5 w-full overflow-hidden rounded-full bg-brand-silver/30">
          <motion.div className="h-full bg-brand-red" animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }} transition={{ duration: 0.4 }} />
        </div>
        <p className="mb-8 text-sm font-medium text-brand-muted">Step {step + 1} of {STEPS.length} · <span className="text-brand-charcoal">{STEPS[step]}</span></p>

        <div className="grid gap-10 lg:grid-cols-[1.6fr_1fr]">
          <div>
            <AnimatePresence mode="wait">
              <motion.div key={step} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.3 }}>
                {step === 0 && (
                  <div>
                    <h2 className="font-serif text-3xl font-bold text-brand-charcoal">What are we detailing?</h2>
                    <div className="mt-6 space-y-4">
                      {vehicles.map((v, vi) => (
                        <div key={vi} className="rounded-2xl border border-brand-silver/40 bg-white p-4">
                          <div className="mb-3 flex items-center justify-between">
                            <span className="text-sm font-semibold text-brand-charcoal">Vehicle {vi + 1}</span>
                            {vehicles.length > 1 && (
                              <button data-testid={`remove-vehicle-${vi}`} onClick={() => setVehicles(vehicles.filter((_, i) => i !== vi))} className="text-brand-muted hover:text-brand-red"><X className="h-4 w-4" /></button>
                            )}
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            {VEHICLES.map((opt) => (
                              <button key={opt.id} data-testid={`vehicle-${vi}-${opt.id}`}
                                onClick={() => setVehicles(vehicles.map((x, i) => (i === vi ? opt.id : x)))}
                                className={`flex flex-col items-center gap-2 rounded-xl border p-4 transition-[border-color] ${v === opt.id ? "border-brand-red bg-brand-red/5" : "border-brand-silver/40 hover:border-brand-red/50"}`}>
                                {opt.id === "van" ? <Truck className="h-6 w-6 text-brand-charcoal" /> : <Car className="h-6 w-6 text-brand-charcoal" />}
                                <span className="text-sm font-medium text-brand-charcoal">{opt.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                      {vehicles.length < 4 && (
                        <button data-testid="add-vehicle" onClick={() => setVehicles([...vehicles, "sedan"])}
                          className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-brand-silver/60 py-4 text-sm font-medium text-brand-muted hover:border-brand-red hover:text-brand-red">
                          <Plus className="h-4 w-4" /> Add another vehicle (multi-vehicle discount applies)
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div>
                    <h2 className="font-serif text-3xl font-bold text-brand-charcoal">Which service?</h2>
                    <div className="mt-6 grid gap-4 sm:grid-cols-2">
                      {SERVICES.map((s) => (
                        <button key={s.id} data-testid={`service-${s.id}`} onClick={() => setService(s.id)}
                          className={`rounded-2xl border p-6 text-left transition-[border-color] ${service === s.id ? "border-brand-red bg-white shadow-[0_18px_40px_-30px_rgba(200,16,46,0.6)]" : "border-brand-silver/40 bg-white hover:border-brand-red/50"}`}>
                          <p className="font-serif text-xl font-bold text-brand-charcoal">{s.label}</p>
                          <p className="mt-1 text-sm text-brand-muted">{s.desc}</p>
                        </button>
                      ))}
                    </div>
                    {service === "ceramic" && (
                      <div className="mt-6">
                        <p className="mb-3 text-sm font-semibold text-brand-charcoal">Coating level</p>
                        <div className="grid gap-3 sm:grid-cols-3">
                          {CERAMIC_TIERS.map((t) => (
                            <button key={t.id} data-testid={`ceramic-tier-${t.id}`} onClick={() => setCeramicTier(t.id)}
                              className={`rounded-xl border p-4 text-left ${ceramicTier === t.id ? "border-brand-red bg-brand-red/5" : "border-brand-silver/40"}`}>
                              <p className="text-sm font-semibold text-brand-charcoal">{t.label}</p>
                              <p className="text-xs text-brand-muted">{t.price}</p>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {step === 2 && (
                  <div>
                    <h2 className="font-serif text-3xl font-bold text-brand-charcoal">What's the condition?</h2>
                    <div className="mt-6 space-y-4">
                      {CONDITIONS.map((c) => (
                        <button key={c.id} data-testid={`condition-${c.id}`} onClick={() => setCondition(c.id)}
                          className={`w-full rounded-2xl border p-5 text-left transition-[border-color] ${condition === c.id ? "border-brand-red bg-white" : "border-brand-silver/40 bg-white hover:border-brand-red/50"}`}>
                          <p className="font-semibold text-brand-charcoal">{c.label}</p>
                          <p className="text-sm text-brand-muted">{c.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div>
                    <h2 className="font-serif text-3xl font-bold text-brand-charcoal">One-time or subscribe & save?</h2>
                    {!subAllowed && <p className="mt-2 text-sm text-brand-muted">Subscriptions apply to Interior & Full details. This service is one-time.</p>}
                    <div className="mt-6 grid gap-3 sm:grid-cols-2">
                      {PLANS.map((p) => {
                        const disabled = !subAllowed && p.id !== "one_time";
                        return (
                          <button key={p.id} data-testid={`plan-${p.id}`} disabled={disabled} onClick={() => setPlan(p.id)}
                            className={`relative rounded-2xl border p-5 text-left transition-[border-color] ${plan === p.id ? "border-brand-red bg-brand-red/5" : "border-brand-silver/40 bg-white hover:border-brand-red/50"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}>
                            {p.badge && <span className="absolute -top-2 right-4 rounded-full bg-brand-red px-2 py-0.5 text-[0.55rem] font-semibold uppercase text-white">{p.badge}</span>}
                            <div className="flex items-center justify-between">
                              <p className="font-semibold text-brand-charcoal">{p.name}</p>
                              {p.off > 0 && <span className="text-xs font-semibold text-brand-red">{p.off}% off</span>}
                            </div>
                            <p className="text-xs text-brand-muted">{p.freq}</p>
                          </button>
                        );
                      })}
                    </div>
                    {plan !== "one_time" && !user && (
                      <div data-testid="subscription-account-prompt" className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-brand-red/40 bg-brand-red/5 p-5">
                        <div className="flex items-start gap-3">
                          <UserPlus className="mt-0.5 h-5 w-5 shrink-0 text-brand-red" />
                          <div>
                            <p className="font-semibold text-brand-charcoal">Create a free account to lock in your recurring discount</p>
                            <p className="text-sm text-brand-muted">Subscriptions save up to 30% every visit — an account lets us track your plan, visits and rewards. You can still continue as a guest.</p>
                          </div>
                        </div>
                        <Link to="/signup" className="btn-red shrink-0" data-testid="subscription-signup-link">Create account</Link>
                      </div>
                    )}
                  </div>
                )}

                {step === 4 && (
                  <div>
                    <h2 className="font-serif text-3xl font-bold text-brand-charcoal">Pick a day, time & place</h2>
                    {availMeta.isMember ? (
                      <div data-testid="priority-active-note" className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-charcoal px-4 py-2 text-xs font-semibold text-white">
                        <Crown className="h-3.5 w-3.5 text-brand-red" /> Priority scheduling active — you can book the next {availMeta.lead} days.
                      </div>
                    ) : (
                      <div data-testid="priority-member-note" className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-brand-red/30 bg-brand-red/5 p-4">
                        <Crown className="h-4 w-4 shrink-0 text-brand-red" />
                        <p className="text-sm text-brand-muted">The next {availMeta.lead} days are reserved for <b className="text-brand-charcoal">Reverence Members</b>. <Link to="/signup" className="font-semibold text-brand-red underline">Join</Link> to unlock priority scheduling.</p>
                      </div>
                    )}
                    <div className="mt-6 grid grid-cols-7 gap-1.5 sm:gap-2">
                      {availability.map((d) => {
                        const [y, m, dd] = d.date.split("-").map(Number);
                        const dt = new Date(y, m - 1, dd);
                        const sel = date === d.date;
                        return (
                          <button key={d.date} data-testid={`booking-day-${d.date}`} disabled={!d.available} onClick={() => setDate(d.date)}
                            className={`flex aspect-square flex-col items-center justify-center rounded-xl border text-center transition-[background-color,border-color] ${sel ? "border-brand-red bg-brand-red text-white" : d.available ? "border-brand-silver/40 bg-white hover:border-brand-red" : "border-transparent bg-brand-chrome/40 text-brand-silver/60 cursor-not-allowed line-through"}`}>
                            <span className="text-[0.5rem] font-semibold uppercase">{WD[dt.getDay()].slice(0, 3)}</span>
                            <span className="text-base font-semibold">{dt.getDate()}</span>
                          </button>
                        );
                      })}
                    </div>
                    <h3 className="mt-8 font-serif text-xl font-bold text-brand-charcoal">Time window</h3>
                    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                      {TIME_SLOTS.map((t) => (
                        <button key={t.id} data-testid={`slot-${t.id}`} onClick={() => setSlot(t.id)}
                          className={`rounded-xl border p-4 text-left ${slot === t.id ? "border-brand-red bg-white" : "border-brand-silver/40 bg-white hover:border-brand-red/50"}`}>
                          <p className="text-sm font-semibold text-brand-charcoal">{t.label}</p>
                          <p className="text-xs text-brand-muted">{t.window}</p>
                        </button>
                      ))}
                    </div>
                    <div className="mt-6 max-w-xs">
                      <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-brand-muted">Postal code</label>
                      <input data-testid="booking-postal" value={postal} onChange={(e) => setPostal(e.target.value)} placeholder="K1A 0B1"
                        className="w-full rounded-xl border border-brand-silver/50 px-4 py-3 text-sm focus:border-brand-red focus:outline-none focus:ring-1 focus:ring-brand-red" />
                    </div>
                  </div>
                )}

                {step === 5 && (
                  <div>
                    <h2 className="font-serif text-3xl font-bold text-brand-charcoal">Almost done — your details</h2>
                    <div className="mt-6 grid gap-4 sm:grid-cols-2">
                      <Field testid="booking-name" label="Full name" value={details.name} onChange={upd("name")} />
                      <Field testid="booking-email" label="Email" type="email" value={details.email} onChange={upd("email")} />
                      <Field testid="booking-phone" label="Phone" value={details.phone} onChange={upd("phone")} />
                      <Field testid="booking-address" label="Service address" value={details.address} onChange={upd("address")} />
                      <div className="sm:col-span-2">
                        <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-brand-muted">Notes (optional)</label>
                        <textarea data-testid="booking-notes" value={details.notes} onChange={upd("notes")} rows={3} placeholder="Gate code, pet hair, specific concerns…"
                          className="w-full rounded-xl border border-brand-silver/50 px-4 py-3 text-sm focus:border-brand-red focus:outline-none focus:ring-1 focus:ring-brand-red" />
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            <div className="mt-10 flex items-center justify-between">
              {step > 0 ? (
                <button data-testid="booking-back" onClick={() => setStep((s) => s - 1)} className="inline-flex items-center gap-2 text-sm font-medium text-brand-muted hover:text-brand-charcoal"><ArrowLeft className="h-4 w-4" /> Back</button>
              ) : <Link to="/" className="text-sm text-brand-muted hover:text-brand-charcoal">Cancel</Link>}
              {step < 5 ? (
                <button data-testid="booking-next" disabled={!canNext} onClick={() => setStep((s) => s + 1)} className="btn-red disabled:opacity-40">Continue <ArrowRight className="h-4 w-4" /></button>
              ) : (
                <button data-testid="booking-submit" disabled={!canNext || busy} onClick={submit} className="btn-red disabled:opacity-40">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Confirm booking <ArrowRight className="h-4 w-4" /></>}</button>
              )}
            </div>
          </div>

          {/* live summary */}
          <div className="lg:sticky lg:top-28 lg:self-start">
            <div data-testid="booking-summary" className="rounded-2xl border border-brand-silver/40 bg-white p-7">
              <p className="overline text-brand-red">{step === 5 ? "Your total" : "Your selection"}</p>
              {user?.membership_tier === "member" && (
                <div data-testid="booking-member-badge" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-brand-charcoal px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-wider text-white">
                  <Crown className="h-3 w-3 text-brand-red" /> Member 10% applied
                </div>
              )}
              {quote?.birthday_applied && (
                <div data-testid="booking-birthday-badge" className="mt-2 ml-2 inline-flex items-center gap-1.5 rounded-full bg-brand-red px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-wider text-white">
                  <Cake className="h-3 w-3" /> Birthday 50% applied
                </div>
              )}
              <div className="mt-5 space-y-2 text-sm">
                <Line label="Vehicles" value={vehicles.length} />
                <Line label="Service" value={SERVICES.find((s) => s.id === service)?.label} />
                <Line label="Plan" value={PLANS.find((p) => p.id === (subAllowed ? plan : "one_time"))?.name} />
                {date && <Line label="Date" value={prettyDate(date)} />}
              </div>
              {step < 5 && (
                <p className="mt-5 rounded-lg bg-brand-offwhite px-3 py-3 text-xs text-brand-muted">
                  Your price is calculated once we know your vehicle and service — you'll see it on the final step, with your discounts already applied.
                </p>
              )}
              {step === 5 && quote && (
                <>
                  <div className="my-5 silver-divider" />
                  <div className="space-y-2 text-sm">
                    {quote.items.map((it, i) => (
                      <div key={i} className="flex justify-between">
                        <span className="text-brand-muted">{it.label}{it.discount_label ? ` · ${it.discount_label}` : ""}</span>
                        <span className={it.discount_amount ? "text-brand-charcoal" : ""}>${it.final.toFixed(2)}</span>
                      </div>
                    ))}
                    {quote.savings > 0 && <div className="flex justify-between text-brand-red"><span>Total savings</span><span>– ${quote.savings.toFixed(2)}</span></div>}
                  </div>
                  <div className="my-4 silver-divider" />
                  <div className="flex items-center justify-between">
                    <span className="font-serif text-lg font-bold text-brand-charcoal">Total today</span>
                    <span className="font-serif text-2xl font-bold text-brand-red" data-testid="summary-total">${quote.total.toFixed(2)} <span className="text-sm font-normal text-brand-muted">+ tax</span></span>
                  </div>
                  {quote.savings > 0 && <p className="mt-3 rounded-lg bg-brand-red/5 px-3 py-2 text-xs text-brand-red" data-testid="summary-savings">You're saving ${quote.savings.toFixed(2)} today</p>}
                  {quote.recurring && (
                    <p className="mt-3 text-xs text-brand-muted">Then ${quote.recurring.per_visit_total.toFixed(2)}/visit recurring ({quote.recurring.discount_pct}% off{quote.recurring.family_fleet ? " incl. family fleet" : ""}).</p>
                  )}
                </>
              )}
              <p className="mt-5 text-xs text-brand-muted">No payment today — pay after your detail. We'll confirm by text. Applicable tax is added to your final invoice.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-brand-muted">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={onChange}
        className="w-full rounded-xl border border-brand-silver/50 px-4 py-3 text-sm focus:border-brand-red focus:outline-none focus:ring-1 focus:ring-brand-red" />
    </div>
  );
}
function Line({ label, value }) {
  return <div className="flex justify-between"><span className="text-brand-muted">{label}</span><span className="font-medium text-brand-charcoal">{value ?? "—"}</span></div>;
}
